import { clsx } from "clsx";
import { Info } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./tooltip";

export interface InfoTooltipProps {
  /** Tooltip body text (short — one or two sentences). */
  text: string;
  /** Optional CTA link text. Rendered only when paired with `linkHref`. */
  linkText?: string;
  /** Optional URL to pair with `linkText`. Opens in a new tab. */
  linkHref?: string;
  /** Overrides the default "More information" sr-only label on the trigger. */
  ariaLabel?: string;
  /** Forwarded to the trigger button wrapper. */
  className?: string;
}

/**
 * Small info-circle trigger that reveals a tooltip on hover / focus. The
 * tooltip optionally hosts a single external link (opens in a new tab) so it
 * can double as a lightweight "learn more" affordance.
 *
 * Built on top of the shared Radix `Tooltip` primitives exported from this
 * package. The trigger is a real `<button>` for keyboard accessibility; the
 * optional anchor inside the tooltip is independently Tab-focusable.
 */
export function InfoTooltip({
  text,
  linkText,
  linkHref,
  ariaLabel = "More information",
  className,
}: InfoTooltipProps) {
  const hasLink = Boolean(linkText && linkHref);

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label={ariaLabel}
            className={clsx(
              "inline-flex items-center justify-center rounded-full",
              "text-zinc-400 hover:text-zinc-600",
              "dark:text-zinc-500 dark:hover:text-zinc-300",
              "transition-colors",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-1 focus-visible:ring-offset-transparent",
              className
            )}
          >
            <Info aria-hidden="true" className="h-4 w-4" />
          </button>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs text-xs leading-snug">
          <p>{text}</p>
          {hasLink && (
            <a
              href={linkHref}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-1 inline-block text-xs text-indigo-300 underline decoration-indigo-400/60 underline-offset-2 hover:text-indigo-200 hover:decoration-indigo-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-300 rounded-sm"
            >
              {linkText}
            </a>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export default InfoTooltip;
